import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = "https://elshowdelasbanqueras.com/show-banqueras-api";

const ENDPOINTS = {
  create: `${API_BASE}/api/episodes_create.php`,
  list: `${API_BASE}/api/episodes_list.php`,
};

function isValidUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function EpisodesManagerServer() {
  const [episodes, setEpisodes] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [form, setForm] = useState({ title: "", description: "", link: "" });

  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState("");

  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit = useMemo(() => {
    return (
      form.title.trim().length >= 3 &&
      form.description.trim().length >= 10 &&
      isValidUrl(form.link.trim())
    );
  }, [form]);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onPickThumb = (e) => {
    const file = e.target.files?.[0] || null;

    // ✅ valida tamaño 3MB
    if (file && file.size > 3 * 1024 * 1024) {
      setErr("La imagen excede 3MB.");
      return;
    }

    setThumbFile(file);

    if (thumbPreview) URL.revokeObjectURL(thumbPreview);
    setThumbPreview(file ? URL.createObjectURL(file) : "");
  };

  // ✅ limpia preview al desmontar
  useEffect(() => {
    return () => {
      if (thumbPreview) URL.revokeObjectURL(thumbPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const safeJson = async (res, label) => {
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      // para depurar más fácil
      console.error("Respuesta NO JSON:", label, text?.slice?.(0, 400));
      throw new Error(`La API no devolvió JSON válido (${label}).`);
    }
  };

  const fetchEpisodes = async () => {
    setListLoading(true);
    setErr("");
    try {
      const res = await fetch(ENDPOINTS.list);
      const data = await safeJson(res, "episodes_list.php");
      if (!res.ok) throw new Error(data?.message || "Error cargando episodios");
      setEpisodes(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message || "Error cargando episodios");
      setEpisodes([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setOk(false);
    setErr("");

    if (!canSubmit) {
      setErr("Completa título, descripción y un link válido (https://...).");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("description", form.description.trim());
      fd.append("link", form.link.trim());

      // ✅ manda con ambos nombres para compatibilidad
      if (thumbFile) {
        fd.append("thumbnail", thumbFile); // tu nombre actual
        fd.append("image", thumbFile);     // por si tu PHP usa "image"
      }

      const res = await fetch(ENDPOINTS.create, {
        method: "POST",
        body: fd,
      });

      const data = await safeJson(res, "episodes_create.php");
      if (!res.ok) throw new Error(data?.message || "No se pudo guardar.");

      setOk(true);
      setForm({ title: "", description: "", link: "" });
      setThumbFile(null);
      if (thumbPreview) URL.revokeObjectURL(thumbPreview);
      setThumbPreview("");

      await fetchEpisodes();
      setTimeout(() => setOk(false), 2200);
    } catch (e2) {
      setErr(e2.message || "Error guardando episodio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="container">
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
          <div>
            <h2 style={{ margin: 0 }}>Gestión de episodios</h2>
            <div className="p" style={{ marginTop: 6, marginBottom: 0, opacity: 0.8 }}>
              URL del formulario: <b>/episodios/nuevo</b>
            </div>
          </div>
          <Link className="btn btn-ghost" to="/">← Volver al Home</Link>
        </div>
      </div>

      <div className="grid grid-2" style={{ gap: 16 }}>
        {/* FORM */}
        <form className="card" onSubmit={onSubmit}>
          <h3 style={{ marginTop: 0 }}>Crear episodio</h3>

          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div
              className="card"
              style={{
                width: 140,
                height: 90,
                borderRadius: 16,
                padding: 0,
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,.06)",
              }}
            >
              {thumbPreview ? (
                <img src={thumbPreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ opacity: 0.75, fontSize: 12 }}>Miniatura</span>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 240 }}>
              <label style={{ display: "block", fontWeight: 900, marginBottom: 6 }}>
                Imagen miniatura (opcional)
              </label>
              <input type="file" accept="image/*" onChange={onPickThumb} />
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                JPG/PNG/WebP • Máx 3MB
              </div>
            </div>
          </div>

          <div style={{ height: 12 }} />

          <label style={{ fontWeight: 900, display: "block", marginBottom: 6 }}>Título *</label>
          <input
            className="card"
            style={{ width: "100%", padding: 12 }}
            name="title"
            value={form.title}
            onChange={onChange}
            placeholder="Ej: EP6 – Tema del día"
            required
          />

          <div style={{ height: 10 }} />

          <label style={{ fontWeight: 900, display: "block", marginBottom: 6 }}>Descripción *</label>
          <textarea
            className="card"
            style={{ width: "100%", padding: 12, minHeight: 120 }}
            name="description"
            value={form.description}
            onChange={onChange}
            placeholder="Cuenta brevemente de qué trata el episodio..."
            required
          />

          <div style={{ height: 10 }} />

          <label style={{ fontWeight: 900, display: "block", marginBottom: 6 }}>Link *</label>
          <input
            className="card"
            style={{ width: "100%", padding: 12 }}
            name="link"
            value={form.link}
            onChange={onChange}
            placeholder="https://www.youtube.com/watch?v=..."
            required
          />

          <div style={{ height: 12 }} />

          <button
            className="btn btn-blue"
            type="submit"
            disabled={saving || !canSubmit}
            style={{ width: "100%", justifyContent: "center", opacity: saving || !canSubmit ? 0.7 : 1 }}
          >
            {saving ? "Guardando..." : "Guardar episodio"}
          </button>

          {err && <div style={{ marginTop: 12, color: "#ff7b7b" }}>❌ {err}</div>}
          {ok && <div style={{ marginTop: 12, color: "var(--green)" }}>✅ Episodio guardado</div>}
        </form>

        {/* LISTADO */}
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0 }}>Episodios publicados</h3>
            <button className="btn btn-ghost" onClick={fetchEpisodes} disabled={listLoading}>
              {listLoading ? "Actualizando..." : "Actualizar"}
            </button>
          </div>

          <div style={{ height: 10 }} />

          {listLoading ? (
            <div style={{ opacity: 0.75 }}>Cargando episodios...</div>
          ) : episodes.length === 0 ? (
            <div style={{ opacity: 0.75 }}>No hay episodios aún.</div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {episodes.map((ep) => {
                // ✅ soporta thumbnail_url o image_url
                const rawPath = (ep.thumbnail_url || ep.image_url || "").replace(/^\/+/, "");
                const thumbSrc = rawPath ? `${API_BASE}/${rawPath}` : "";

                return (
                  <div key={ep.id} className="card" style={{ padding: 12, borderRadius: 16 }}>
                    <div style={{ display: "flex", gap: 12 }}>
                      <div
                        style={{
                          width: 140,
                          height: 90,
                          borderRadius: 14,
                          overflow: "hidden",
                          background: "rgba(255,255,255,.06)",
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        {thumbSrc ? (
                          <img src={thumbSrc} alt={ep.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <span style={{ opacity: 0.7, fontSize: 12 }}>Sin miniatura</span>
                        )}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 900, lineHeight: 1.2 }}>{ep.title}</div>
                        <div style={{ opacity: 0.85, marginTop: 6, fontSize: 13, lineHeight: 1.35 }}>
                          {ep.description}
                        </div>

                        <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                          <a className="btn btn-ghost" href={ep.link} target="_blank" rel="noreferrer">
                            Ver episodio
                          </a>
                        </div>

                        <div style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
                          {ep.created_at ? new Date(ep.created_at).toLocaleString() : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
